import type { FC } from "react";
import type React from "react";
import { useCallback, useMemo } from "react";

import { useTranslations } from "@ui/providers/useTranslations";

import type { DropdownIndicatorProps, GroupBase, OptionProps, SingleValueProps } from "@des/react-select";
import Select, { components } from "@des/react-select";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { DevicePhoneIcon } from "@jti/brand-icons/device-phone";
import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import type { SelectOptionType } from "@jti/fe-common";

import styles from "./InternationalTelephone.module.scss";

export type CountrySelectWithIconProps = {
  value: string;
  options: SelectOptionType[];
  iconComponent: React.ElementType;
  unicodeFlags: boolean;
  editButtonLabel?: string;
  onChange: (value: string) => void;
  isEditable: boolean;
  handleOnEditClick: () => void;
  mode?: "standard" | "editable";
  hasDefaultCountry?: boolean;
};

const DropdownIndicatorComponent = (
  props: DropdownIndicatorProps<SelectOptionType, false, GroupBase<SelectOptionType>>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon aria-hidden="true" />
    </components.DropdownIndicator>
  );
};

export const CountrySelectWithIcon: FC<CountrySelectWithIconProps> = ({
  value,
  options,
  iconComponent: Icon,
  unicodeFlags,
  onChange,
  editButtonLabel,
  isEditable,
  handleOnEditClick,
  hasDefaultCountry,
}) => {
  const { __ } = useTranslations();
  const displayedEditButtonLabel = editButtonLabel || __("text_input.default_edit_label");
  const countryNameForInternationalValue = "ZZ";

  const filteredOptions = useMemo(() => {
    if (hasDefaultCountry) {
      return options.filter((option) => option.value !== countryNameForInternationalValue);
    }
    return options;
  }, [options, hasDefaultCountry]);

  const handleCountryCodeChange = useCallback(
    (field: SelectOptionType | null) => {
      if (field) {
        onChange(field.value === countryNameForInternationalValue ? "" : field.value);
      }
    },
    [onChange]
  );

  const SingleValueComponent = (props: SingleValueProps<SelectOptionType, false, GroupBase<SelectOptionType>>) => (
    <components.SingleValue {...props}>
      <div className={styles.countryFlag}>
        {value ? (
          <Icon aria-hidden country={value} aspectRatio={unicodeFlags ? 1 : undefined} label={props.data.label} />
        ) : (
          <DevicePhoneIcon className={styles.phoneIcon} aria-hidden="true" />
        )}
      </div>
    </components.SingleValue>
  );

  const OptionComponent = (props: OptionProps<SelectOptionType, false, GroupBase<SelectOptionType>>) => (
    <components.Option {...props} className={props.data.value === value ? styles.selectedOption : ""}>
      {props.label}
      {props.data.value === value && <UiCheckIcon className={styles.checkmarkIcon} aria-hidden="true" />}
    </components.Option>
  );

  const EMPTY_OPTION = { value: "", label: "" };
  const selectedOption = useMemo(() => {
    if (!value) {
      return EMPTY_OPTION;
    }
    return filteredOptions.find((option) => option.value === value) || EMPTY_OPTION;
  }, [value, filteredOptions]);

  return (
    <>
      <Select<SelectOptionType, false>
        options={filteredOptions}
        value={selectedOption}
        components={{
          Option: OptionComponent,
          SingleValue: SingleValueComponent,
          DropdownIndicator: DropdownIndicatorComponent,
          IndicatorSeparator: () => null,
        }}
        classNamePrefix="react-select"
        onChange={handleCountryCodeChange}
        className={styles.inputContainer}
        aria-label={__("internationalPhone.country_code_aria_label")}
      />
      {isEditable && (
        <button onClick={handleOnEditClick} className={styles.editButton} type="button" data-testid="editButton">
          {displayedEditButtonLabel}
        </button>
      )}
    </>
  );
};
